/** Testing-phase booking rules — single client source of truth. */

export const CREATE_MAX_DAYS_AHEAD = 7;
/** Restore after testing phase: export const CREATE_MAX_DAYS_AHEAD = 183; */
export const JOIN_MAX_DAYS_AHEAD = 6;
export const OPEN_JOIN_CUTOFF_HOURS = 24;
export const CONFIRMED_JOIN_CUTOFF_HOURS = 12;
export const MIN_SEATS_FLOOR = 2;
export const MIN_SEATS_CAP = 4;
export const CREATE_MIN_DAYS_AHEAD = 2;
/** Hours before departure that a confirmed trip's authorised cards are charged. */
export const CHARGE_HOURS_BEFORE_DEPARTURE = 36;

export function getJoinCutoffHours(trip) {
  return trip?.status === 'confirmed'
    ? CONFIRMED_JOIN_CUTOFF_HOURS
    : OPEN_JOIN_CUTOFF_HOURS;
}

/**
 * Departures are stored as Nairobi wall-clock time: `travel_date` + `time_window_start`
 * describe when the vehicle leaves on the corridor, not when the browser's clock says.
 *
 * `new Date('2026-08-14T07:30:00')` — no offset — is parsed in the *browser's* zone, so
 * a rider in London computed a departure 2h off, and one in New York 8h off. That was
 * invisible while the rules were client-only and self-consistent. It stops being
 * invisible now that enforce_trip_request_creation_rules applies the same window
 * server-side with `AT TIME ZONE 'Africa/Nairobi'`: the two disagree, and a booking the
 * form accepts gets rejected by the database (or vice versa).
 *
 * EAT has been UTC+3 with no DST since 1960, so a fixed offset is exact here and avoids
 * depending on Intl timezone data being present in every runtime.
 */
export const CORRIDOR_UTC_OFFSET = '+03:00';

export function parseDepartureInstant(travelDate, timeStart) {
  if (!travelDate || !timeStart) return null;
  // Postgres `time` serialises as HH:MM:SS, but callers sometimes pass HH:MM.
  const time = String(timeStart).length === 5 ? `${timeStart}:00` : timeStart;
  const departure = new Date(`${travelDate}T${time}${CORRIDOR_UTC_OFFSET}`);
  return Number.isFinite(departure.getTime()) ? departure : null;
}

export function getHoursUntilDeparture(travelDate, timeStart) {
  const departure = parseDepartureInstant(travelDate, timeStart);
  // Preserve the previous contract: unparseable input yields NaN, and every
  // comparison against it is false.
  if (!departure) return NaN;
  return (departure.getTime() - Date.now()) / (1000 * 60 * 60);
}

/** Stripe manual-capture authorization holds expire after 7 days. */
export const STRIPE_AUTH_HOLD_MAX_DAYS = 7;

export function evaluateTripAuthorization(trip, role, reserveStatus) {
  const hoursUntilDeparture = getHoursUntilDeparture(trip.travel_date, trip.time_window_start);
  const status = reserveStatus ?? trip.status;
  const maxDaysAhead = role === 'creator' ? CREATE_MAX_DAYS_AHEAD : JOIN_MAX_DAYS_AHEAD;

  if (status !== 'open' && status !== 'confirmed') {
    return { ok: false, reason: 'trip_closed' };
  }
  if (hoursUntilDeparture > maxDaysAhead * 24) {
    return { ok: false, reason: 'too_far_ahead' };
  }
  if (hoursUntilDeparture > STRIPE_AUTH_HOLD_MAX_DAYS * 24) {
    return { ok: false, reason: 'too_far_ahead' };
  }
  if (role === 'creator' && hoursUntilDeparture < CREATE_MIN_DAYS_AHEAD * 24) {
    return { ok: false, reason: 'too_soon' };
  }
  if (role === 'joiner') {
    if (status === 'open' && hoursUntilDeparture <= OPEN_JOIN_CUTOFF_HOURS) {
      return { ok: false, reason: 'join_cutoff_open' };
    }
    if (status === 'confirmed' && hoursUntilDeparture <= CONFIRMED_JOIN_CUTOFF_HOURS) {
      return { ok: false, reason: 'join_cutoff_confirmed' };
    }
  }
  return { ok: true, hoursUntilDeparture };
}

export function evaluateTripJoinability(trip, reserveStatus) {
  return evaluateTripAuthorization(trip, 'joiner', reserveStatus);
}

export function creationRejectionMessage(reason) {
  switch (reason) {
    case 'too_far_ahead':
      return `Shared trips can be started up to ${CREATE_MAX_DAYS_AHEAD} days before departure during our testing phase.`;
    case 'too_soon':
    case 'departure_too_soon':
      return 'Choose a later date or a later time slot — shared trips need at least 48 hours before departure.';
    case 'commitment_min_seat_mismatch':
      return 'Committed trips use “Just us”. Uncommitted trips need at least one other rider in the minimum.';
    case 'invalid_group_size':
      return 'Group size must be between 1 and 4.';
    case 'trip_closed':
      return 'This trip cannot be created in its current state.';
    default:
      return null;
  }
}

/** Map PostgREST / trigger errors that embed creation window codes. */
export function extractCreationRejectionReason(errorOrMessage) {
  const text = typeof errorOrMessage === 'string'
    ? errorOrMessage
    : [errorOrMessage?.message, errorOrMessage?.details, errorOrMessage?.code, errorOrMessage?.reason]
      .filter(Boolean)
      .join(' ');
  if (!text) return null;
  if (text.includes('commitment_min_seat_mismatch')) return 'commitment_min_seat_mismatch';
  if (text.includes('invalid_group_size')) return 'invalid_group_size';
  if (text.includes('departure_too_soon')) return 'departure_too_soon';
  if (text.includes('too_soon')) return 'too_soon';
  return null;
}

/** Map PostgREST / trigger errors that embed join_cutoff_* codes. */
export function extractJoinRejectionReason(errorOrMessage) {
  const text = typeof errorOrMessage === 'string'
    ? errorOrMessage
    : [errorOrMessage?.message, errorOrMessage?.details, errorOrMessage?.code, errorOrMessage?.reason]
      .filter(Boolean)
      .join(' ');
  if (!text) return null;
  if (text.includes('join_cutoff_open')) return 'join_cutoff_open';
  if (text.includes('join_cutoff_confirmed')) return 'join_cutoff_confirmed';
  return null;
}

export function joinRejectionMessage(reason) {
  switch (reason) {
    case 'too_far_ahead':
      return 'Joining opens within 6 days of departure while card holds are limited.';
    case 'join_cutoff_open':
      return 'This open trip stops accepting riders 24 hours before departure.';
    case 'join_cutoff_confirmed':
      return 'This confirmed trip stops accepting extra riders 12 hours before departure.';
    case 'trip_closed':
      return 'This trip is no longer accepting riders.';
    case 'trip_full':
      return 'This trip is full — no seats are available.';
    case 'already_joined':
      return 'You have already joined this trip.';
    case 'seat_reservation_failed':
      return 'We could not reserve a seat. Please try again.';
    case 'participant_not_found':
      return 'Your booking session expired. Please start again.';
    case 'forbidden':
      return 'You do not have permission to join this trip.';
    default:
      return 'This trip is no longer joinable. Please refresh and try again.';
  }
}

/** True when confirm_participant_payment returned captured-but-not-admitted. */
export function isManualResolutionConfirmResult(result) {
  return result?.manual_resolution === true || result?.payment_status === 'manual_resolution';
}

export function manualResolutionMessage(reason) {
  switch (reason) {
    case 'trip_full':
    case 'seat_reservation_failed':
      return 'Your payment was received, but the trip filled up before your booking could be confirmed. Our team will review and follow up — please do not pay again.';
    case 'join_cutoff_open':
    case 'join_cutoff_confirmed':
      return 'Your payment was received, but the join window had closed. Our team will review and follow up — please do not pay again.';
    case 'trip_closed':
      return 'Your payment was received, but this trip is no longer accepting riders. Our team will review and follow up — please do not pay again.';
    case 'invalid_points':
      return 'Your payment was received, but your pickup or drop-off could not be confirmed. Our team will review and follow up — please do not pay again.';
    case 'pricing_error':
    case 'captured_amount_mismatch':
      return 'Your payment was received, but the fare could not be verified. Our team will review and follow up — please do not pay again.';
    default:
      return 'Your payment was received, but your booking needs manual review. Please contact support — do not pay again.';
  }
}

export function getJoinDeadlineSummary(trip) {
  if (trip.status === 'confirmed') {
    return `Confirmed — extra riders welcome until ${CONFIRMED_JOIN_CUTOFF_HOURS}h before departure`;
  }
  return `Open — must reach minimum by ${OPEN_JOIN_CUTOFF_HOURS}h before departure or trip cancels`;
}

export const TESTING_PHASE_BOOKING_COPY =
  `Start shared trips up to ${CREATE_MAX_DAYS_AHEAD} days ahead. A trip must reach its minimum ${OPEN_JOIN_CUTOFF_HOURS} hours before departure or it is cancelled and everyone is notified. Confirmed trips can take extra riders up to ${CONFIRMED_JOIN_CUTOFF_HOURS} hours before departure.`;

/** Riders still needed beyond seats already filled. */
export function getAdditionalRidersNeeded(seatsFilled, minSeatCount) {
  return Math.max(0, (minSeatCount ?? MIN_SEATS_FLOOR) - (seatsFilled ?? 0));
}

/** Allowed "other riders needed" values for a given group size (total min stays within floor–cap). */
export function getAdditionalOptionsForGroupSize(groupSize, committed = false) {
  const size = Math.max(1, groupSize ?? 1);
  if (committed) {
    return [0];
  }
  const minAdditional = 1;
  const maxAdditional = Math.max(0, MIN_SEATS_CAP - size);
  const options = [];
  for (let n = minAdditional; n <= maxAdditional; n += 1) {
    options.push(n);
  }
  return options;
}

export function additionalToMinSeatCount(groupSize, additionalRidersNeeded, committed = false) {
  if (committed) {
    return Math.max(1, groupSize ?? 1);
  }
  return Math.max(MIN_SEATS_FLOOR, (groupSize ?? 1) + (additionalRidersNeeded ?? 0));
}

export function minSeatCountToAdditional(groupSize, minSeatCount) {
  return Math.max(0, (minSeatCount ?? MIN_SEATS_FLOOR) - (groupSize ?? 1));
}

/** Short labels for the create-form dropdown. */
export function formatRidersShort(additional) {
  if (additional === 0) return 'Just us';
  return `+${additional}`;
}

export function formatConfirmAtRiders(minSeatCount) {
  const n = minSeatCount ?? MIN_SEATS_FLOOR;
  return `Confirms at ${n} rider${n !== 1 ? 's' : ''}`;
}

export function formatRidersProgress(seatsFilled, minSeatCount) {
  const filled = seatsFilled ?? 0;
  const min = minSeatCount ?? MIN_SEATS_FLOOR;
  if (filled >= min) return 'Confirmed';
  const more = getAdditionalRidersNeeded(filled, min);
  return `${more} more to confirm`;
}
